import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Center,
  CircularProgress,
  Divider,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { Select, SingleValue } from "chakra-react-select";
import { FormikProps } from "formik";
import { useMemo } from "react";
import { P, match } from "ts-pattern";
import { useSummaryData } from "../../services/projectService";
import { ProjectID } from "../../types/project";
import { SiteMeasurements } from "../../types/summaryData";
import { SummaryFormValues, useSummaryForm } from "../../types/summaryForm";

export function SummaryView() {
  const formik = useSummaryForm();

  return (
    <VStack padding={12} alignItems={"stretch"} gap={4}>
      <SummaryForm formik={formik} />
      <Divider />
      <SummaryDataDisplay project={formik.values.project} />
    </VStack>
  );
}

function SummaryForm({ formik }: { formik: FormikProps<SummaryFormValues> }) {
  const options = useMemo(
    () => [
      { value: "groundWater", label: "Ground Water" },
      { value: "drinkingWater", label: "Drinking Water" },
      { value: "surfaceWater", label: "Surface Water" },
    ],
    [],
  );

  const value = useMemo(
    () => options.find((x: any) => x.value === formik.values.project),
    [options, formik.values.project],
  );

  return (
    <Select
      size="md"
      placeholder="Select Project"
      options={options}
      onChange={(x: SingleValue<{ value: string }>) => formik.setFieldValue("project", x?.value)}
      value={value}
      selectedOptionColorScheme="brand"
    />
  );
}

function SummaryDataDisplay({ project }: { project: ProjectID }) {
  const query = useSummaryData(project);

  return match(query)
    .with({ isLoading: true }, () => <CircularProgress isIndeterminate color="brand.400" />)
    .with({ isError: true }, () => <Text color="red">Error loading summary data</Text>)
    .with({ data: P.nullish }, () => <></>)
    .otherwise(({ data }) => (
      <VStack alignItems={"stretch"}>
        <Accordion allowMultiple>
          {data.sitesMeasurements.map((siteMeasurements) => (
            <SiteSummary siteMeasurements={siteMeasurements} key={siteMeasurements.site} />
          ))}
        </Accordion>
      </VStack>
    ));
}

function SiteSummary({ siteMeasurements }: { siteMeasurements: SiteMeasurements }) {
  return (
    <AccordionItem>
      <h2>
        <AccordionButton bg="brand">
          <Box as="span" flex="1" textAlign="left">
            <HStack gap={4}>
              <Text>{siteMeasurements.site}</Text>
              <Center height="30px">
                <Divider orientation="vertical" />
              </Center>
              <Text>{siteMeasurements.purpose}</Text>
            </HStack>
          </Box>
          <AccordionIcon />
        </AccordionButton>
      </h2>

      <AccordionPanel>
        <TableContainer>
          <Table variant="striped" size="sm" colorScheme="darkBrown">
            <Thead>
              <Tr>
                <Th>Analyte</Th>
                <Th>Samples</Th>
                <Th>Start Date</Th>
                <Th>End Date</Th>
              </Tr>
            </Thead>
            <Tbody>
              {siteMeasurements.analyteMeasurement.map((measurement) => (
                <Tr key={measurement.analyte}>
                  <Td>{measurement.analyte}</Td>
                  <Td>{measurement.numberOfSamples}</Td>
                  <Td>{measurement.startDate.toLocaleString()}</Td>
                  <Td>{measurement.endDate.toLocaleString()}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </AccordionPanel>
    </AccordionItem>
  );
}
